<template>
  <div class="header-tags">
    <WTooltip :label="demandsDescription" position="bottom-start">
      <CDemandTag
        :demands="demands"
        :last-sync="lastSync"
        class="header-tags__tag-clickable"
        @click="openCasePillar"
      />
    </WTooltip>
    <WTooltip label="Ver consultas" position="bottom">
      <HTag
        v-if="appointments"
        color="blue"
        class="header-tags__tag-clickable"
        @click="openAppointmentPillar"
      >
        <WIcon icon="icPaper" size="medium" /> {{ appointments }}
      </HTag>
    </WTooltip>
    <WTooltip
      v-for="tag in tags"
      :key="tag.type"
      :label="tag.label"
      position="bottom-start"
    >
      <HTag color="blue" class="header-tags__tag">
        <WIcon :icon="tag.icon" size="medium" />
      </HTag>
    </WTooltip>
  </div>
</template>

<script>
import { WIcon, WTooltip } from '@alice-health/wonderland-vue'

import { CDemandTag } from '@/components/channels/atoms'
import { HTag } from '@/components/shared/atoms'
import { HEALTH_DATA_TYPE } from '@/models/clinicalProfile/healthData'
import { DEMANDS_SEVERITY_MAP } from '@/models/demands'
import { PILLAR_ID } from '@/models/pillars'
import {
  checkNewDemand,
  getDemandDescription,
  getDemandLabel
} from '@/utils/demands'
import Logger, { EventModel, EventName } from '@/wrappers/logger'

export default {
  components: { HTag, CDemandTag, WIcon, WTooltip },
  inject: ['pillarId'],
  props: {
    healthTags: {
      type: Array,
      default: () => []
    },
    channel: {
      type: Object,
      default: () => ({})
    },
    lastSync: {
      type: Date,
      default: undefined
    }
  },
  data() {
    return {
      isNewSpan:
        " - <span style='color: var(--sys-color-surface-container-active-variant)'>Novo</span>",
      disableTooltipText: 'Os filtros não estão disponíveis na visão de canais'
    }
  },
  computed: {
    demands() {
      return this.channel?.demands
    },
    tags() {
      return this.healthTags.map(tag => {
        return {
          ...tag,
          icon: this.getTagIcon(tag.type)
        }
      })
    },
    demandsDescription() {
      if (!this.channel?.demands?.length) {
        return ''
      }
      const bullet = ' &#8226; '
      const description = this.channel.demands.reduce((acc, current) => {
        acc += bullet + getDemandLabel(current) + ': '
        acc += getDemandDescription(current)
        acc += current.severity
          ? `${bullet} ${DEMANDS_SEVERITY_MAP[current.severity]}`
          : ''
        acc += this.isNew(current) ? this.isNewSpan : ''
        acc += '<br/>'
        return acc
      }, '')
      return `<div style='max-width: 340px'> ${description} </div>`
    },
    appointments() {
      return this.channel?.appointmentIds?.length || 0
    },
    caseIds() {
      return this.channel?.demands?.map(demand => demand.caseId)
    }
  },
  methods: {
    getTagIcon(type) {
      const availableIcons = {
        [HEALTH_DATA_TYPE.ALLERGY]: 'icBug',
        [HEALTH_DATA_TYPE.PREGNANCY]: 'icBaby'
      }

      return availableIcons[type] || 'icBandaid'
    },
    isNew(demand) {
      return checkNewDemand(demand, this.lastSync)
    },
    async openAppointmentPillar() {
      await this.$store.dispatch('pillars/timeline/setOtherFilters', {
        filterTitle: 'Mostrando consultas do canal',
        filterIds: this.channel?.appointmentIds,
        filterName: this.channel.name,
        disableTooltipText: this.disableTooltipText
      })
      this.$store.dispatch('pillars/openPillarFromTab', {
        actualPillarId: this.pillarId,
        tabToOpen: PILLAR_ID.TIMELINE,
        closeUnrelatedPillar: false
      })
      Logger.addLog(EventName.USER_INTERACTION_FILTER, {
        model: EventModel.TIMELINE,
        data: {
          filters: ['filter_timeline_by_channel']
        }
      })
    },
    openCasePillar() {
      this.$store.dispatch('pillars/openPillarFromTab', {
        actualPillarId: this.pillarId,
        tabToOpen: PILLAR_ID.DEMAND,
        closeUnrelatedPillar: false
      })
      this.$store.dispatch('pillars/demands/setFilterByChannel', {
        filterTitle: 'Mostrando demandas do canal',
        filterName: this.channel.name,
        channelId: this.channel.id,
        disableTooltipText: this.disableTooltipText
      })
      Logger.addLog(EventName.USER_INTERACTION_FILTER, {
        model: EventModel.DEMANDS,
        data: {
          filters: ['filter_demand_by_channel']
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.header-tags {
  margin-left: var(--gl-spacing-01);
  gap: var(--gl-spacing-02);
  display: flex;
  flex-wrap: wrap;

  &__tag {
    &-clickable {
      cursor: pointer;
    }
  }
}
</style>
